import { ZION_APP_LABEL } from "./app-labels.js";
import { z } from "zod";
//#region src/autoscale.ts
/**
* Custom-autoscale shared contract (custom-autoscale plan, WS-1 [SHARED CONTRACT]).
*
* Single source of truth for:
*   - Seam A: the bounded metric MENU an app may request (D8) + the `custom:<name>`
*     gauge grammar. These exact strings are consumed VERBATIM by the activator
*     `/admin/signals` (WS-2), the collectors (WS-5), and the harness (WS-3).
*   - Seam B: the `ScaleContext` the platform assembles + the user function
*     contract (`ScaleFn` scale-up / `ScaleDownFn` scale-down / `AutoscaleAlgorithm`
*     / `AutoscalerRegistry`) the app writes and `AutoscalerRuntime` (WS-3) runs.
*   - Seam E metadata: the KEDA `external` trigger `metadata` map keys the operator
*     (WS-4) stamps and the harness (WS-3) reads via `scalerMetadata`, plus the
*     direct-replica-trick metric name/target (D5).
*   - The `default` algorithm sentinel + the algorithm-name grammar (D4).
*
* Zod is provided for runtime validation of the menu + requested-metric grammar
* (WS-3/WS-6 reuse). The function types are pure TS (they hold callables — no zod).
*/
/**
* The bounded set of platform-collected metrics an autoscaler may REQUEST (D8).
* Only the metrics an algorithm lists in `FixtureAutoscaler.requests` are
* collected and passed (least-work). App-custom gauges are addressed separately
* as `custom:<name>` (see {@link CUSTOM_METRIC_PREFIX} / {@link requestedMetricSchema}).
*
* Value semantics (frozen; producers in parentheses):
*   - `gpuUtilization`   — 0..100 avg across the service's pods (dcgm-exporter, WS-5)
*   - `cpuUtilization`   — 0..100 avg (metrics-server, WS-5)
*   - `memoryBytes`      — avg working-set bytes per pod (metrics-server, WS-5)
*   - `httpInFlight`     — current concurrent in-flight requests (activator, WS-2)
*   - `httpQueueDepth`   — requests queued incl. held-at-zero (activator, WS-2)
*   - `requestRate`      — requests/sec over the poll window (activator, WS-2)
*   - `currentReplicas`  — ready replicas now (k8s, WS-3/WS-4)
*/
const AUTOSCALE_METRICS = [
	"gpuUtilization",
	"cpuUtilization",
	"memoryBytes",
	"httpInFlight",
	"httpQueueDepth",
	"requestRate",
	"currentReplicas"
];
/** Runtime validator for a menu metric name (WS-3/WS-6). */
const autoscaleMetricSchema = z.enum(AUTOSCALE_METRICS);
/** Prefix for an app-custom gauge pushed via `reportLoad()` (Seam D). A requested
*  metric of the form `custom:<name>` opts the harness into forwarding that gauge. */
const CUSTOM_METRIC_PREFIX = "custom:";
/**
* Max length of a custom metric NAME — shared publish↔runtime bound (DEC-34). The
* runtime `reportLoad()` gauge-name check (WS-3) caps at this length; the publish
* grammar (`requestedMetricSchema`, the CG-4 JSON-schema `maxLength`) MUST bind the
* SAME cap so a `custom:<129+ char>` name is never publish-green but runtime-400'd
* (silent fail-safe). Bound on the BARE-name schema `.max()` AND reused as the shared
* runtime constant. 128 mirrors the runtime `GAUGE_NAME_MAX_LEN` (WS-3).
*/
const MAX_METRIC_NAME_LEN = 128;
/**
* Grammar for the `<name>` in `custom:<name>` — lowercase-friendly gauge label
* (letters, digits, `_`, `.`, `-`; may lead with a digit). Kept deliberately
* narrow so the name is safe as a JSON key, a metric label, and a URL segment:
* the charset is bounded to `[a-z0-9._-]` with NO newline / control / whitespace
* chars — **injection-safe** (R2-M2 / DEC-33).
*
* **This is the ONE shared gauge/custom-metric NAME grammar (R2-M2 / DEC-33).** The
* SAME pattern governs a custom metric name at BOTH ends, so publish and runtime
* cannot disagree:
*   - PUBLISH: the manifest `custom:<name>` grammar (CG-4 schema `^custom:…` +
*     {@link CUSTOM_METRIC_TOKEN_PATTERN}, which embeds this grammar) and validate-app
*     (WS-6).
*   - RUNTIME: the harness `reportLoad()` gauge-name check (WS-3) validates the bare
*     `<name>` with THIS constant.
* **WS-3 imports `CUSTOM_METRIC_NAME_PATTERN` VERBATIM — it MUST NOT define its own
* (stricter) `GAUGE_NAME_PATTERN`.** A stricter runtime pattern (e.g. one that
* forbids `.`/`-` or a leading digit) would 400 a publish-valid gauge like
* `custom:queue.depth` at runtime, silently dropping the signal (fail-safe-holds).
* Frozen here as the single source of truth so publish == runtime.
*/
const CUSTOM_METRIC_NAME_PATTERN = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/;
/** Full `custom:<name>` token pattern (prefix + grammar). */
const CUSTOM_METRIC_TOKEN_PATTERN = /^custom:[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/;
/**
* A REQUESTED metric — either a menu name (Seam A) or a `custom:<name>` gauge.
* This is the element type of `FixtureAutoscaler.requests` and mirrors exactly
* the JSON-schema `anyOf` in `fixture.schema.json` `$defs/autoscaler.requests`
* (CG-4). WS-3 reuses it to validate the requested set at harness boot. The
* `custom:<name>` branch is length-bounded to `MAX_METRIC_NAME_LEN` chars past the
* prefix (DEC-34 — shared publish↔runtime cap).
*/
const requestedMetricSchema = z.union([autoscaleMetricSchema, z.string().regex(CUSTOM_METRIC_TOKEN_PATTERN).max(135)]);
/**
* Reserved sentinel for `FixtureService.scaling.algorithm`. Omitting `scaling`,
* omitting `algorithm`, or setting `algorithm: 'default'` all mean "today's
* HTTPScaledObject request-rate scaling" (zero regression, D4). MUST NOT be used
* as a key in the `autoscalers` map.
*/
const DEFAULT_AUTOSCALE_ALGORITHM = "default";
/**
* Grammar for a custom algorithm name (an `autoscalers` map key + the value of
* `scaling.algorithm`). Kebab-case, same shape as a fixture/service name so it is
* safe to embed in a derived k8s resource name (the harness Deployment/Service,
* WS-4). `'default'` matches this pattern but is RESERVED (above).
*/
const AUTOSCALE_ALGORITHM_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;
/**
* The keys of the KEDA `external` trigger `metadata` map — the ONLY channel the
* operator (WS-4) passes to the harness (WS-3) at reconcile (KEDA forwards it as
* `scalerMetadata`). Frozen here so producer (WS-4 emits the ScaledObject) and
* consumer (WS-3 reads `scalerMetadata[...]`) cannot drift. Everything dynamic the
* harness fetches itself (Seam F).
* FINAL SET per DEC-18 + DEC-26 (one harness Deployment serves many services, so ALL
* per-service inputs ride the metadata). KEDA metadata values are ALWAYS strings —
* the operator stamps strings; the harness parses.
*   - `scalerAddress` — `<harness-svc>:<port>` the ScaledObject's `scalerAddress`.
*   - `algorithm`     — the algorithm name to dispatch to (a value from the app's registry).
*   - `appKey`        — the app SLUG (DEC-13; == `zion.witness.ai/app` pod label). NOT a UUID.
*   - `version`       — the app-version.
*   - `serviceName`   — the service's `svc.name` (human/label use only; NOT DP-unique — do NOT
*     use it to key routes/signals; use `signalsKey`).
*   - `signalsKey`    — the DP-unique wire key = the operator-rendered stable BACKEND SERVICE
*     name (DEC-26). The activator (WS-2) indexes routes + signals by it; the harness (WS-3)
*     queries `/admin/signals/{signalsKey}`; WS-4 writes it as the route-ConfigMap key + stamps
*     it here. Disambiguates multiple services in one fixture (`svc.name` is not DP-unique).
*   - `targetWorkload` — the service's k8s Deployment NAME (DEC-18/DEC-19); the harness
*     reads `currentReplicas` from its `apps/v1` `status.readyReplicas` and selects pods by it.
*   - `minReplicas` / `maxReplicas` — the resolved clamp band (DEC-7). Harness `parseInt`s
*     them → `ScaleContext.minReplicas`/`maxReplicas` (Seam B) + clamps the result (D7). The
*     operator ALSO sets the ScaledObject native `minReplicaCount`/`maxReplicaCount`.
*   - `requests` — the algorithm's requested metric set (DEC-18), COMMA-JOINED
*     (`AutoscaleMetric | custom:<name>`); the harness splits it and collects ONLY these (D8).
*   - `stabilizationSeconds` — OPTIONAL scale-down stabilization override (from
*     `FixtureAutoscaler.scaleDown.stabilizationSeconds`, D6); absent ⇒ platform default.
*/
const AUTOSCALE_SCALER_METADATA_KEYS = {
	scalerAddress: "scalerAddress",
	algorithm: "algorithm",
	appKey: "appKey",
	version: "version",
	serviceName: "serviceName",
	signalsKey: "signalsKey",
	targetWorkload: "targetWorkload",
	minReplicas: "minReplicas",
	maxReplicas: "maxReplicas",
	requests: "requests",
	stabilizationSeconds: "stabilizationSeconds"
};
/**
* KEDA direct-replica trick (D5): the harness serves `GetMetricSpec.target = 1`
* and `GetMetrics.value = <clamped desired replicas>`, so KEDA's `ceil(value/target)`
* yields the function's answer directly. WS-3 uses these on the gRPC responses;
* WS-4 puts {@link AUTOSCALE_METRIC_NAME} in the trigger's `metricName`.
*/
const AUTOSCALE_METRIC_NAME = "desiredReplicas";
const AUTOSCALE_METRIC_TARGET = 1;
/**
* Runtime validator for `FixtureService.scaling` (DEC-7). The
* {@link FixtureServiceScaling} INTERFACE (fixture-manifest.ts) is the manifest
* TYPE; THIS schema is the runtime validator WS-6 (`zion-app validate-app` + CP
* publish) runs per service to enforce the two rules Ajv/JSON-schema cannot:
*   1. `maxReplicas` is REQUIRED when a CUSTOM algorithm is set (algorithm present
*      and !== 'default') — the mandatory cost ceiling (DEC-7). A `'default'`/
*      omitted service may omit it.
*   2. `minReplicas <= maxReplicas` when both are present.
* The `minReplicas` DEFAULT (omitted ⇒ 0 when the service declares
* `topology.scaleToZero`, else 1) is NOT applied here — it needs sibling
* `topology` visibility and is resolved downstream by WS-4/WS-6.
*
* `z.infer<typeof fixtureServiceScalingSchema>` MUST stay structurally equal to
* the `FixtureServiceScaling` interface (a type-level drift guard is asserted in
* the tests — see test summary T-1).
*/
const fixtureServiceScalingSchema = z.object({
	algorithm: z.string().regex(AUTOSCALE_ALGORITHM_NAME_PATTERN).optional(),
	minReplicas: z.number().int().nonnegative().optional(),
	maxReplicas: z.number().int().positive().optional()
}).strict().refine((s) => !(s.algorithm != null && s.algorithm !== "default") || s.maxReplicas != null, {
	message: "scaling.maxReplicas is required when a custom scaling.algorithm is set (cost ceiling, DEC-7)",
	path: ["maxReplicas"]
}).refine((s) => s.minReplicas == null || s.maxReplicas == null || s.minReplicas <= s.maxReplicas, {
	message: "scaling.minReplicas must be <= scaling.maxReplicas",
	path: ["minReplicas"]
});
/**
* The collector wire constants (DEC-14). WS-3 (harness) imports these directly;
* WS-5's Helm chart MIRRORS `dcgm.{serviceName,port,metricsPath,gpuUtilField}` into
* `values.yaml` and a **parity test** asserts chart == this constant (Helm can't
* import TS). Single source of truth so WS-3 ↔ WS-5 can't drift.
*
* **This literal is pinned VERBATIM by WS-5 §7a** — field names
* (`serviceName`/`port`/`metricsPath`/`gpuUtilField`/`podSelectorLabelKeys`) must
* match exactly or WS-5's `test/autoscale-collectors-contract-parity.spec.ts`
* fails the build. Do not rename.
*
* `podSelectorLabelKeys` are the pod-label KEYS the harness selects target pods by;
* the VALUES come from the ScaledObject metadata (DEC-13: `.app` value = the
* `appKey` slug, `.service` = `serviceName`, `.version` = `version`).
*/
const AUTOSCALE_COLLECTORS = {
	dcgm: {
		serviceName: "dcgm-exporter",
		port: 9400,
		metricsPath: "/metrics",
		gpuUtilField: "DCGM_FI_DEV_GPU_UTIL"
	},
	podSelectorLabelKeys: {
		service: "app.kubernetes.io/name",
		app: ZION_APP_LABEL,
		version: "zion.witness.ai/version"
	}
};
/** Zod mirror of {@link FixtureAutoscaler} — gives `requests ⊆ menu` at the zod
*  layer (each item is a menu name or `custom:<name>`, via `requestedMetricSchema`),
*  in addition to the JSON-schema `anyOf`. Used inside
*  {@link autoscalerManifestCrossRefSchema}. */
const fixtureAutoscalerSchema = z.object({
	runtime: z.enum(["node", "python"]),
	requests: z.array(requestedMetricSchema).min(1),
	scaleDown: z.object({ stabilizationSeconds: z.number().int().nonnegative().optional() }).strict().optional()
}).strict();
/**
* Manifest-level cross-reference validation (DEC-15 — WS-1 owns this, NOT WS-6):
* every service's `scaling.algorithm` (non-`'default'`) MUST name a key in the
* manifest's `autoscalers` map, and `'default'` MUST NOT be an `autoscalers` key.
* Ajv/JSON-schema cannot cross-reference two sibling manifest maps; zod
* `.superRefine` CAN. Consumed by `zion-app validate` at publish AND by the
* operator (WS-4) fail-closed at reconcile.
*
* Takes the two relevant manifest slices; unknown manifest keys are stripped
* (default zod object behavior) — callers use `.safeParse` for the issue list, not
* the parsed value. `requests ⊆ menu` rides `fixtureAutoscalerSchema` per value.
*/
const autoscalerManifestCrossRefSchema = z.object({
	autoscalers: z.record(z.string(), fixtureAutoscalerSchema).optional(),
	services: z.array(z.object({
		name: z.string(),
		scaling: fixtureServiceScalingSchema.optional()
	})).optional()
}).superRefine((m, ctx) => {
	const keys = new Set(Object.keys(m.autoscalers ?? {}));
	if (keys.has("default")) ctx.addIssue({
		code: "custom",
		path: ["autoscalers", DEFAULT_AUTOSCALE_ALGORITHM],
		message: `'${DEFAULT_AUTOSCALE_ALGORITHM}' is reserved and must not be an autoscalers key`
	});
	(m.services ?? []).forEach((svc, i) => {
		const algo = svc.scaling?.algorithm;
		if (algo != null && algo !== "default" && !keys.has(algo)) ctx.addIssue({
			code: "custom",
			path: [
				"services",
				i,
				"scaling",
				"algorithm"
			],
			message: `scaling.algorithm '${algo}' does not match any key in the autoscalers map`
		});
	});
});
//#endregion
export { AUTOSCALE_ALGORITHM_NAME_PATTERN, AUTOSCALE_COLLECTORS, AUTOSCALE_METRICS, AUTOSCALE_METRIC_NAME, AUTOSCALE_METRIC_TARGET, AUTOSCALE_SCALER_METADATA_KEYS, CUSTOM_METRIC_NAME_PATTERN, CUSTOM_METRIC_PREFIX, CUSTOM_METRIC_TOKEN_PATTERN, DEFAULT_AUTOSCALE_ALGORITHM, MAX_METRIC_NAME_LEN, autoscaleMetricSchema, autoscalerManifestCrossRefSchema, fixtureAutoscalerSchema, fixtureServiceScalingSchema, requestedMetricSchema };

//# sourceMappingURL=autoscale.js.map